import React from 'react';
import { FlatList, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDeals } from '../../src/data';
import { GROUPS, type Group, groupOf, price, sourceNames, timeAgo } from '../../src/format';
import type { Deal } from '../../src/types';

/**
 * B안 — taste-skill / 안티슬롭. 같은 정보 설계 위에 다른 미학을 얹는다.
 *
 * taste-skill 이 지적하는 AI 기본값을 의도적으로 피한다.
 *  - 차가운 회색 + 파란 액센트는 한국 앱이 전부 쓰는 값이다. 따뜻한 종이색 바탕과
 *    채도를 낮춘 흙빛 액센트 하나만 쓴다.
 *  - 똑같은 줄이 반복되는 대칭을 깬다. 맨 위 한 건은 가격을 디스플레이 타입으로 크게 세우고,
 *    나머지는 조밀한 목록으로 흐른다.
 *  - 제목이 아니라 가격이 주인공이다. 딜 앱에서 사람이 먼저 보는 것은 숫자다.
 * 모션은 낮게 유지한다 — 작업 중인 화면이라 연출을 볼 이유가 없다.
 */
const P = {
  paper: '#F4F1EC',
  sheet: '#FBF9F6',
  ink: '#191512',
  ink2: '#6E6459',
  ink3: '#9C9287',
  rule: '#E2DCD2',
  clay: '#A8432A',
  moss: '#4A6B4F',
} as const;

export default function DesignB() {
  const insets = useSafeAreaInsets();
  const deals = useDeals();
  const [group, setGroup] = React.useState<Group>('전체');
  const shown = React.useMemo(
    () => (group === '전체' ? deals : deals.filter((d) => groupOf(d) === group)),
    [deals, group],
  );
  const [lead, ...rest] = shown;

  return (
    <View style={{ flex: 1, backgroundColor: P.paper }}>
      <FlatList
        data={rest}
        keyExtractor={(d) => d.id}
        contentContainerStyle={{ paddingBottom: 24 }}
        ItemSeparatorComponent={() => <View style={s.rule} />}
        renderItem={({ item }) => <Row deal={item} />}
        ListHeaderComponent={
          <View>
            <View style={[s.head, { paddingTop: insets.top + 16 }]}>
              <Text style={s.kicker}>{shown.length}건 · 5분마다 갱신</Text>
              <Text style={s.h1}>오늘의 딜</Text>
            </View>
            <View style={s.nav}>
              {GROUPS.map((g) => (
                <Pressable key={g} onPress={() => setGroup(g)}>
                  <Text style={[s.navItem, g === group && s.navOn]}>{g}</Text>
                </Pressable>
              ))}
            </View>
            {lead ? <Lead deal={lead} /> : null}
          </View>
        }
      />
    </View>
  );
}

function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

/** 맨 위 한 건. 가격을 디스플레이 타입으로 세워 목록의 리듬을 깬다. */
function Lead({ deal }: { deal: Deal }) {
  const off = discount(deal);
  return (
    <Pressable style={({ pressed }) => [s.lead, pressed && { opacity: 0.85 }]}>
      <Text style={s.leadMeta}>
        {deal.mall ?? '판매처 미확인'}
        <Text style={{ color: P.ink3 }}>
          {'   '}
          {sourceNames(deal.sources)}
          {deal.posted_at ? `   ${timeAgo(deal.posted_at)}` : ''}
        </Text>
      </Text>
      <Text style={s.leadPrice}>{price(deal)}</Text>
      <Text style={s.leadTitle} numberOfLines={3}>
        {deal.product}
      </Text>
      <View style={{ flexDirection: 'row', gap: 14, alignItems: 'baseline' }}>
        {off !== null ? <Text style={s.leadOff}>{off}% 싸게</Text> : null}
        {deal.shipping_krw === 0 ? <Text style={s.leadShip}>무료배송</Text> : null}
      </View>
    </Pressable>
  );
}

function Row({ deal }: { deal: Deal }) {
  const off = discount(deal);
  return (
    <Pressable style={({ pressed }) => [s.row, pressed && { backgroundColor: P.sheet }]}>
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <Text style={s.title} numberOfLines={2}>
          {deal.product}
        </Text>
        <Text style={s.meta} numberOfLines={1}>
          {deal.mall ?? '판매처 미확인'}
          <Text style={{ color: P.ink3 }}>
            {'   '}
            {deal.posted_at ? timeAgo(deal.posted_at) : ''}
          </Text>
        </Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 2 }}>
        <Text style={s.price}>{price(deal)}</Text>
        {off !== null ? (
          <Text style={s.off}>{off}%</Text>
        ) : deal.shipping_krw === 0 ? (
          <Text style={s.ship}>무료배송</Text>
        ) : null}
      </View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  head: { paddingHorizontal: 22, paddingBottom: 14, gap: 4 },
  kicker: { fontSize: 12, color: P.ink3, letterSpacing: 0.6, fontVariant: ['tabular-nums'] },
  h1: { fontSize: 30, fontWeight: '800', color: P.ink, letterSpacing: -1.1 },
  // 알약 칩 대신 흐르는 글자 내비. 선택은 색이 아니라 굵기와 밑줄로 읽힌다
  nav: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, paddingHorizontal: 22, paddingBottom: 16 },
  navItem: { fontSize: 14, color: P.ink3 },
  navOn: { color: P.clay, fontWeight: '700', textDecorationLine: 'underline', textDecorationColor: P.clay },

  lead: { paddingHorizontal: 22, paddingTop: 18, paddingBottom: 22, backgroundColor: P.sheet, gap: 6,
    borderTopWidth: 1, borderBottomWidth: 1, borderColor: P.rule },
  leadMeta: { fontSize: 12, color: P.ink2 },
  leadPrice: { fontSize: 46, fontWeight: '800', color: P.ink, letterSpacing: -2.4, fontVariant: ['tabular-nums'], marginTop: 2 },
  leadTitle: { fontSize: 16, lineHeight: 23, color: P.ink2, marginTop: 2 },
  leadOff: { fontSize: 13, fontWeight: '800', color: P.clay },
  leadShip: { fontSize: 13, color: P.moss, fontWeight: '600' },

  row: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 22, paddingVertical: 14 },
  rule: { height: 1, marginLeft: 22, backgroundColor: P.rule },
  title: { fontSize: 15, lineHeight: 20, color: P.ink },
  meta: { fontSize: 12, color: P.ink2 },
  price: { fontSize: 18, fontWeight: '800', color: P.ink, letterSpacing: -0.6, fontVariant: ['tabular-nums'] },
  off: { fontSize: 12, fontWeight: '800', color: P.clay },
  ship: { fontSize: 12, color: P.moss },
});
